import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber,
  Tag, Popconfirm, message, Space, Statistic, Row, Col, Card, Divider,
} from "antd";
import { ArrowLeft, Plus, FileText } from "lucide-react";
import dayjs from "dayjs";
import {
  useInvoiceList, useInvoiceStats, useInvoiceCreate, useInvoiceUpdate,
  useInvoiceDelete, useInvoiceStatus, useInvoicePayment, useFinClients,
  useAllPOs, useAllQuotes,
} from "../../../api-hooks/finance";

const STATUS_TAG: Record<string, string> = {
  draft: "default", sent: "blue", overdue: "orange", written_off: "red", void: "volcano",
};
const STATUS_OPTS = [
  { value: "draft", label: "Draft" }, { value: "sent", label: "Sent" },
  { value: "overdue", label: "Overdue" }, { value: "written_off", label: "Written Off" },
  { value: "void", label: "Void" },
];
const TYPE_OPTS = [
  { value: "standard", label: "Standard" }, { value: "credit_note", label: "Credit Note" },
  { value: "pro_forma", label: "Pro Forma" }, { value: "recurring", label: "Recurring" },
];
const PAY_OPTS = [
  { value: "bank_transfer", label: "Bank Transfer" }, { value: "card", label: "Card" },
  { value: "cash", label: "Cash" }, { value: "cheque", label: "Cheque" },
  { value: "stripe", label: "Stripe" }, { value: "gocardless", label: "GoCardless" },
];

export default function InvoicesPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const cid = Number(companyId);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();

  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("");
  const [page, setPage] = useState(1);
  const params = { search: search || undefined, status: statusF || undefined, page, limit: 20 };

  const { data: res, isLoading } = useInvoiceList(cid, params);
  const { data: stats } = useInvoiceStats(cid);
  const { data: clients = [] } = useFinClients(cid);
  const { data: allPOs = [] } = useAllPOs(cid);
  const { data: allQuotes = [] } = useAllQuotes(cid);
  // invoice source flow
  const [invFlow, setInvFlow] = useState<"standalone" | "from_quote" | "from_po">("standalone");

  const invoices = res?.data ?? [];
  const total = res?.total ?? 0;

  const [modal, setModal] = useState<{ open: boolean; rec: any | null; mode: "edit" | "status" | "payment" | "create" }>({ open: false, rec: null, mode: "create" });
  const [form] = Form.useForm();
  const [lineItems, setLineItems] = useState<any[]>([{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);

  const cb = (msg: string) => ({
    onSuccess: () => { msgApi.success(msg); closeModal(); },
    onError: (e: any) => msgApi.error(e?.message || "Error"),
  });

  const create  = useInvoiceCreate(cid, cb("Invoice created."));
  const update  = useInvoiceUpdate(cid, cb("Invoice updated."));
  const remove  = useInvoiceDelete(cid, cb("Deleted."));
  const status  = useInvoiceStatus(cid, cb("Status updated."));
  const payment = useInvoicePayment(cid, cb("Payment recorded."));

  function openCreate() {
    setModal({ open: true, rec: null, mode: "create" });
    setInvFlow("standalone");
    setLineItems([{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);
    form.resetFields();
    form.setFieldsValue({ invoice_type: "standard", status: "draft", issue_date: dayjs(), due_date: dayjs().add(30, "day"), vat_rate: 20 });
  }

  function openEdit(r: any) {
    setModal({ open: true, rec: r, mode: "edit" });
    form.setFieldsValue({
      title: r.title, client_id: r.client_id, invoice_type: r.invoice_type,
      status: r.status, issue_date: r.issue_date ? dayjs(r.issue_date) : null,
      due_date: r.due_date ? dayjs(r.due_date) : null, vat_rate: r.vat_rate,
      discount_amount: r.discount_amount, notes: r.notes, terms: r.terms,
    });
    setLineItems(r.line_items?.length ? r.line_items.map((li: any) => ({ description: li.description, quantity: li.quantity, unit_price: li.unit_price, vat_rate: li.vat_rate })) : [{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);
  }

  function openStatus(r: any) { setModal({ open: true, rec: r, mode: "status" }); form.resetFields(); }
  function openPayment(r: any) { setModal({ open: true, rec: r, mode: "payment" }); form.resetFields(); form.setFieldsValue({ payment_method: "bank_transfer", payment_date: dayjs(), amount: r.amount_outstanding }); }
  function closeModal() { setModal({ open: false, rec: null, mode: "create" }); form.resetFields(); }

  function handleSubmit(vals: any) {
    const payload: any = {
      ...vals,
      issue_date: vals.issue_date?.format("YYYY-MM-DD"),
      due_date: vals.due_date?.format("YYYY-MM-DD"),
      line_items: lineItems,
    };
    if (modal.mode === "create") create.mutate(payload);
    else if (modal.mode === "edit") update.mutate({ id: modal.rec.id, ...payload });
    else if (modal.mode === "status") status.mutate({ id: modal.rec.id, status: vals.status });
    else if (modal.mode === "payment") payment.mutate({ id: modal.rec.id, ...vals, payment_date: vals.payment_date?.format("YYYY-MM-DD") });
  }

  const columns = [
    { title: "Invoice #", dataIndex: "invoice_number", key: "n", render: (v: string, r: any) => <div><div className="font-medium text-sm">{v}</div><div className="text-xs text-slate-400 truncate max-w-[140px]">{r.title}</div></div> },
    { title: "Client", dataIndex: "client_name", key: "c", render: (v: string) => v || "—" },
    { title: "Type", dataIndex: "invoice_type", key: "t", render: (v: string) => <Tag>{v?.replace("_", " ")}</Tag> },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={STATUS_TAG[v]}>{v?.toUpperCase()}</Tag> },
    { title: "Issue", dataIndex: "issue_date", key: "i", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Due", dataIndex: "due_date", key: "d", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Total", dataIndex: "total", key: "total", render: (v: number) => `£${Number(v || 0).toFixed(2)}` },
    { title: "Outstanding", dataIndex: "amount_outstanding", key: "ao", render: (v: number) => <span className={Number(v) > 0 ? "text-red-600 font-medium" : "text-green-600"}>£{Number(v || 0).toFixed(2)}</span> },
    {
      title: "", key: "act", render: (_: any, r: any) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Button size="small" onClick={() => openStatus(r)}>Status</Button>
          <Button size="small" type="primary" ghost onClick={() => openPayment(r)}>Pay</Button>
          <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}>
            <Button size="small" danger>Del</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const statsCards = stats ? [
    { title: "Draft", value: stats.draft ?? 0, color: "#6b7280" },
    { title: "Sent", value: stats.sent ?? 0, color: "#3b82f6" },
    { title: "Overdue", value: stats.overdue ?? 0, color: "#f97316" },
    { title: "Void", value: stats.void ?? 0, color: "#ef4444" },
  ] : [];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-violet-600 hover:text-violet-800 flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>
          <div>
            <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2"><FileText size={20} className="text-violet-600" /> Invoices — Company #{cid}</h1>
            <p className="text-xs text-slate-500">{total} invoices</p>
          </div>
        </div>
        <Button type="primary" icon={<Plus size={16} />} onClick={openCreate} style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>New Invoice</Button>
      </div>

      {stats && (
        <Row gutter={12} className="mb-4">
          {statsCards.map(s => (
            <Col key={s.title} span={6}>
              <Card size="small" className="text-center"><Statistic title={s.title} value={s.value} valueStyle={{ color: s.color, fontSize: 20 }} /></Card>
            </Col>
          ))}
        </Row>
      )}

      <div className="flex gap-3 mb-4">
        <Input.Search placeholder="Search invoices…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" allowClear />
        <Select value={statusF || undefined} onChange={v => setStatusF(v ?? "")} placeholder="All Statuses" options={[{ value: "", label: "All" }, ...STATUS_OPTS]} className="w-40" allowClear />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <Table rowKey="id" dataSource={invoices} columns={columns} loading={isLoading} size="small"
          pagination={{ current: page, pageSize: 20, total, onChange: setPage, showTotal: t => `${t} invoices` }} />
      </div>

      <Modal open={modal.open} onCancel={closeModal} footer={null} destroyOnClose width={modal.mode === "create" || modal.mode === "edit" ? 640 : 400}
        title={modal.mode === "create" ? "New Invoice" : modal.mode === "edit" ? `Edit ${modal.rec?.invoice_number}` : modal.mode === "status" ? "Update Status" : "Record Payment"}>
        <Form form={form} layout="vertical" size="middle" onFinish={handleSubmit} className="pt-2">
          {(modal.mode === "create" || modal.mode === "edit") && (<>
            {modal.mode === "create" && (
              <div className="mb-4 flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Invoice Source:</span>
                <Select
                  value={invFlow}
                  onChange={(v) => { setInvFlow(v); form.setFieldValue("quote_id", undefined); form.setFieldValue("po_id", undefined); }}
                  options={[
                    { value: "standalone", label: "Standalone" },
                    { value: "from_quote", label: "From Quotation" },
                    { value: "from_po", label: "From Purchase Order" },
                  ]}
                  style={{ width: 220 }}
                  size="small"
                />
              </div>
            )}
            {modal.mode === "create" && invFlow === "from_quote" && (
              <Form.Item name="quote_id" label="Linked Quotation" rules={[{ required: true, message: "Select a quotation" }]}>
                <Select showSearch allowClear placeholder="Select quotation…" optionFilterProp="label"
                  options={(allQuotes as any[]).map((q: any) => ({ value: q.id, label: `${q.quote_number} — ${q.title || q.client_name || ""}` }))} />
              </Form.Item>
            )}
            {modal.mode === "create" && invFlow === "from_po" && (
              <Form.Item name="po_id" label="Linked Purchase Order" rules={[{ required: true, message: "Select a PO" }]}>
                <Select showSearch allowClear placeholder="Select PO…" optionFilterProp="label"
                  options={(allPOs as any[]).map((po: any) => ({ value: po.id, label: `${po.po_number}${po.supplier_name ? ` — ${po.supplier_name}` : ""}` }))} />
              </Form.Item>
            )}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="title" label="Title"><Input placeholder="Invoice title" /></Form.Item>
              <Form.Item name="client_id" label="Client"><Select showSearch allowClear options={(clients as any[]).map((c: any) => ({ value: c.id, label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email }))} /></Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="invoice_type" label="Type"><Select options={TYPE_OPTS} /></Form.Item>
              <Form.Item name="status" label="Status"><Select options={STATUS_OPTS} /></Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="issue_date" label="Issue Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
              <Form.Item name="due_date" label="Due Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            </div>
            <Divider plain className="my-2">Line Items</Divider>
            {lineItems.map((li, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1 mb-2 items-start">
                <div className="col-span-5"><Input placeholder="Description" value={li.description} onChange={e => { const n = [...lineItems]; n[idx].description = e.target.value; setLineItems(n); }} /></div>
                <div className="col-span-2"><InputNumber className="w-full" placeholder="Qty" min={0} value={li.quantity} onChange={v => { const n = [...lineItems]; n[idx].quantity = v; setLineItems(n); }} /></div>
                <div className="col-span-2"><InputNumber className="w-full" placeholder="Price" min={0} value={li.unit_price} onChange={v => { const n = [...lineItems]; n[idx].unit_price = v; setLineItems(n); }} /></div>
                <div className="col-span-2"><InputNumber className="w-full" placeholder="VAT%" min={0} max={100} value={li.vat_rate} onChange={v => { const n = [...lineItems]; n[idx].vat_rate = v; setLineItems(n); }} /></div>
                <div className="col-span-1"><Button size="small" danger onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))}>×</Button></div>
              </div>
            ))}
            <Button size="small" onClick={() => setLineItems([...lineItems, { description: "", quantity: 1, unit_price: 0, vat_rate: 20 }])} className="mb-4">+ Line Item</Button>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="vat_rate" label="Default VAT %"><InputNumber className="w-full" min={0} max={100} /></Form.Item>
              <Form.Item name="discount_amount" label="Discount (£)"><InputNumber className="w-full" min={0} /></Form.Item>
            </div>
            <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
            <Form.Item name="terms" label="Terms"><Input.TextArea rows={2} /></Form.Item>
          </>)}

          {modal.mode === "status" && (
            <Form.Item name="status" label="New Status" rules={[{ required: true }]}>
              <Select options={STATUS_OPTS} />
            </Form.Item>
          )}

          {modal.mode === "payment" && (<>
            <Form.Item name="amount" label="Amount (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0.01} prefix="£" /></Form.Item>
            <Form.Item name="payment_method" label="Method"><Select options={PAY_OPTS} /></Form.Item>
            <Form.Item name="payment_date" label="Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          </>)}

          <div className="flex gap-2 pt-2">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
              loading={create.isPending || update.isPending || status.isPending || payment.isPending}>
              {modal.mode === "create" ? "Create" : modal.mode === "payment" ? "Record Payment" : "Save"}
            </Button>
            <Button onClick={closeModal}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
